import { Head, usePage } from '@inertiajs/react';
import { Globe } from 'lucide-react';
import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { DirectVideoPlayer } from '@/components/direct-video-player';
import { Github, Linkedin } from '@/components/icons';
import { YouTubePlayer } from '@/components/youtube-player';
import type { ProjectMember, SharedProps, VotesShow } from '@/types';
import { getInitials, isDefaultLogo } from './components/logo';
import { OnelinerText } from './components/OnelinerText';
import { UpvoteButton } from './components/UpvoteButton';
import { VoteLayout } from './components/VoteLayout';

function extractGithubUsername(github: string | null | undefined): string {
  if (!github) return '';
  if (github.includes('github.com')) {
    const parts = github.split('/');
    return parts[parts.length - 1] || github;
  }
  return github;
}

function getGithubAvatarUrl(github: string | null | undefined): string | null {
  const username = extractGithubUsername(github);
  if (!username) return null;
  return `https://github.com/${username}.png`;
}

function MemberOverlay({
  member,
  font,
}: {
  member: ProjectMember;
  font: string;
}) {
  return (
    <>
      <span
        className={`text-center font-medium ${font} text-background text-xs leading-tight`}
      >
        {member.fullName}
      </span>
      {(member.github || member.linkedin) && (
        <div className="flex items-center justify-center gap-2">
          {member.github && (
            <a
              href={member.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-background transition-colors hover:text-background/70"
            >
              <Github className="h-4 w-4" />
            </a>
          )}
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-background transition-colors hover:text-background/70"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          )}
        </div>
      )}
    </>
  );
}

export default function Show({
  edition,
  project,
  videoId,
  directVideoUrl,
  defaultLogoHash,
  hasGoogleAccount,
  isBelowThreshold,
  title,
  description,
  ogImageUrl,
}: VotesShow) {
  const { auth } = usePage<SharedProps>().props;
  const v25 = edition.variant === '25';
  const font = v25 ? 'font-title' : 'font-sans';
  const placeholderLogo = isDefaultLogo(project.logoHash, defaultLogoHash);
  const onelinerText = v25
    ? project.oneliner
    : project.onelinerShort || project.oneliner;

  const upvote = (
    <UpvoteButton
      edition={edition}
      projectSlug={project.slug}
      projectName={project.name}
      voted={project.hasVoted}
      count={project.voteCount}
      isAuthenticated={!!auth.user}
      hasGoogleAccount={hasGoogleAccount}
      isBelowThreshold={isBelowThreshold}
    />
  );

  const oneliner = onelinerText ? (
    <OnelinerText
      variant={edition.variant}
      text={onelinerText}
      fullText={v25 ? null : project.oneliner}
      projectName={project.name}
      projectLogoUrl={project.logoUrl}
      projectTrackName={project.trackName}
      isDefaultLogo={placeholderLogo}
    />
  ) : null;

  const trackBadge = project.trackName ? (
    <span
      className={`inline-block w-fit bg-primary/10 px-3 py-1 ${font} text-primary text-sm`}
    >
      {project.trackName}
    </span>
  ) : null;

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImageUrl} />
      </Head>
      <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="space-y-6">
          {/* Project Header - Mobile Layout */}
          <div className="flex flex-col gap-4 sm:hidden">
            {/* Logo and Title Row */}
            <div className="flex items-center gap-4">
              {project.logoUrl && !placeholderLogo ? (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-primary/20 bg-primary/5">
                  <img
                    src={project.logoUrl}
                    alt={project.name}
                    width={96}
                    height={96}
                    className="h-full w-full object-contain p-2"
                  />
                </div>
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-primary/20 bg-primary/10">
                  <span className={`font-bold ${font} text-2xl text-primary`}>
                    {getInitials(project.name)}
                  </span>
                </div>
              )}
              <h1 className={`flex-1 font-bold ${font} text-2xl text-primary`}>
                {project.name}
              </h1>
            </div>

            {/* Oneliner and Track */}
            <div className="flex flex-col gap-2">
              {oneliner}
              {trackBadge}
            </div>

            {/* Upvote Button */}
            {upvote}
          </div>

          {/* Project Header - Desktop Layout */}
          <div
            className={
              v25
                ? 'hidden items-center gap-4 sm:flex'
                : 'hidden items-stretch gap-4 sm:flex'
            }
          >
            {project.logoUrl && !placeholderLogo ? (
              <div
                className={
                  v25
                    ? 'flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-primary/20 bg-primary/5 lg:h-24 lg:w-24'
                    : 'flex w-24 shrink-0 items-center justify-center self-stretch overflow-hidden rounded-lg border-2 border-primary/20 bg-primary/5 lg:w-28'
                }
              >
                <img
                  src={project.logoUrl}
                  alt={project.name}
                  width={v25 ? 96 : 120}
                  height={v25 ? 96 : 120}
                  className={
                    v25
                      ? 'h-full w-full object-contain p-2'
                      : 'h-full w-full object-contain p-3'
                  }
                />
              </div>
            ) : (
              <div
                className={
                  v25
                    ? 'flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border-2 border-primary/20 bg-primary/10 lg:h-24 lg:w-24'
                    : 'flex w-24 shrink-0 items-center justify-center self-stretch rounded-lg border-2 border-primary/20 bg-primary/10 lg:w-28'
                }
              >
                <span
                  className={`font-bold ${font} text-3xl text-primary lg:text-4xl`}
                >
                  {getInitials(project.name)}
                </span>
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
              <h1
                className={`font-bold ${font} text-3xl text-primary sm:text-4xl`}
              >
                {project.name}
              </h1>
              {oneliner}
              {trackBadge}
            </div>
            <div className={v25 ? 'shrink-0' : 'shrink-0 self-center'}>
              {upvote}
            </div>
          </div>

          {/* Video Player */}
          {videoId ? (
            <YouTubePlayer
              videoId={videoId}
              startAt={project.videoStartAt}
              endAt={project.videoEndAt}
            />
          ) : (
            directVideoUrl && (
              <DirectVideoPlayer
                src={directVideoUrl}
                poster={directVideoUrl.replace(/\.mp4(\?|$)/, '-poster.jpg$1')}
              />
            )
          )}

          {/* Action Buttons and Team Members */}
          <div className="flex flex-col items-center gap-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              {project.repoUrl && (
                <a
                  href={project.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 border-2 border-primary/30 bg-transparent px-4 py-2 ${font} text-primary transition-all hover:border-primary hover:bg-primary hover:text-background`}
                >
                  <Github className="h-4 w-4" />
                  ver código
                </a>
              )}
              {project.deployUrl && (
                <a
                  href={project.deployUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 border-2 border-primary/30 bg-transparent px-4 py-2 ${font} text-primary transition-all hover:border-primary hover:bg-primary hover:text-background`}
                >
                  <Globe className="h-4 w-4" />
                  ver aplicación
                </a>
              )}
            </div>

            {/* Team Members */}
            {project.members.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4">
                {project.members.map((member) => {
                  const avatarUrl = getGithubAvatarUrl(member.github);
                  return (
                    <div key={member.id} className="group relative h-24 w-24">
                      {avatarUrl ? (
                        <>
                          <img
                            src={avatarUrl}
                            alt={member.fullName}
                            width={96}
                            height={96}
                            className="h-24 w-24 rounded-full border-2 border-primary/20 object-cover transition-all group-hover:border-primary"
                          />
                          {/* Overlay with text and icons */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-full bg-primary/90 px-2 py-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <MemberOverlay member={member} font={font} />
                          </div>
                        </>
                      ) : (
                        <div
                          className={`flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-primary/20 bg-primary/20 font-bold ${font} text-2xl text-primary transition-all group-hover:border-primary group-hover:bg-primary/90`}
                        >
                          <span className="text-2xl transition-opacity group-hover:opacity-0">
                            {member.fullName.charAt(0).toUpperCase()}
                          </span>
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 py-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <MemberOverlay member={member} font={font} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Description */}
          {project.description && (
            <div className="prose prose-invert prose-lg max-w-none border-primary/20 border-l-2 pl-4">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  img: ({ src, alt, ...props }) => {
                    // Handle relative paths - resolve to GitHub raw URL or use project logoUrl
                    let imageSrc = typeof src === 'string' ? src : '';
                    if (imageSrc.startsWith('./') && project.repoUrl) {
                      // Convert relative path to GitHub raw URL
                      const repoMatch = project.repoUrl.match(
                        /github\.com\/([^/]+)\/([^/]+)/,
                      );
                      if (repoMatch) {
                        const [, owner, repo] = repoMatch;
                        const filePath = imageSrc.replace('./', '');
                        imageSrc = `https://raw.githubusercontent.com/${owner}/${repo}/main/${filePath}`;
                      }
                    }
                    // If it's project-logo.png, use the stored logoUrl
                    if (
                      imageSrc.includes('project-logo.png') &&
                      project.logoUrl
                    ) {
                      imageSrc = project.logoUrl;
                    }
                    return (
                      <img
                        src={imageSrc}
                        alt={alt}
                        className="h-auto max-h-96 max-w-full rounded-lg object-contain"
                        {...props}
                      />
                    );
                  },
                }}
              >
                {project.description}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

Show.layout = (page: ReactNode) => <VoteLayout>{page}</VoteLayout>;
